import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { KonfliktLoesung } from "../lib/terminKonflikt";
import { angebotFrageAnstossen } from "../lib/angebotFrage";
import { apiGet, apiPost, apiPatch, apiPut, apiDelete, apiUpload } from "@vimaana/ui";
import type { Termin, TerminOrte, TeamMember, Owner, Projekt, Kunde, ProjektDetail, ProjektKennzahlen, KundeDetail, KundeCreateBody, KundePatchBody, TeamMemberCreateBody, TeamMemberPatchBody, ProjektCreateBody, ProjektPatchBody, ProjektUebersicht, Ausgabe, AusgabeCreateBody, AusgabePatchBody, StundenZeile, SlotPatchBody, StundeCreateBody, TagesEvent, EventCreateBody, EventPatchBody, Pauschale, PauschaleCreateBody, ProjektTermin, ProjektBeleg, ProjektAuftragswert, RechnungKandidat, ProjektAkte, ProjektAkteCreateBody, ProjektAkteEintrag, ProjektAufnahme, AufnahmePhase } from "../lib/types";

/** Termine + Slots eines Datumsbereichs (Inhaber-Sicht: alle Personen/Projekte). */
export function useKalender(from: string, to: string) {
  return useQuery<Termin[]>({
    queryKey: ["kalender", from, to],
    queryFn: () => apiGet<Termin[]>(`/api/kontor/kalender?from=${from}&to=${to}`),
    staleTime: 15_000,
  });
}

export function useTeamMembers(includeInactive = false) {
  return useQuery<TeamMember[]>({
    queryKey: ["team", "members", includeInactive],
    queryFn: () => apiGet<TeamMember[]>(`/api/kontor/team/members?include_inactive=${includeInactive}`),
    staleTime: 5 * 60_000,
  });
}

export function useTeamOwners() {
  return useQuery<Owner[]>({
    queryKey: ["team", "owners"],
    queryFn: () => apiGet<Owner[]>("/api/kontor/team/owners"),
    staleTime: 5 * 60_000,
  });
}

export function useProjekte(includeAll = false) {
  return useQuery<Projekt[]>({
    queryKey: ["projekte", includeAll],
    queryFn: () => apiGet<Projekt[]>(`/api/kontor/projekte${includeAll ? "?include_all=true" : ""}`),
    staleTime: 60_000,
  });
}

export function useKunden() {
  return useQuery<Kunde[]>({
    queryKey: ["kunden"],
    queryFn: () => apiGet<Kunde[]>("/api/kontor/kunden"),
    staleTime: 5 * 60_000,
  });
}

export function useProjektDetail(id?: string) {
  return useQuery<ProjektDetail>({
    queryKey: ["projekt", id],
    queryFn: () => apiGet<ProjektDetail>(`/api/kontor/projekt/${id}`),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useProjektKennzahlen(id?: string) {
  return useQuery<ProjektKennzahlen>({
    queryKey: ["projekt", id, "kennzahlen"],
    queryFn: () => apiGet<ProjektKennzahlen>(`/api/kontor/projekt/${id}/kennzahlen`),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useProjektAkte(id?: string) {
  return useQuery<ProjektAkte>({
    queryKey: ["projekt", id, "akte"],
    queryFn: () => apiGet<ProjektAkte>(`/api/kontor/projekt/${id}/akte`),
    enabled: !!id,
    staleTime: 15_000,
  });
}

/** Eine gelieferte Menge — Beleg für die Entscheidung, keine Rechnungsgrundlage. */
export type Lieferposition = {
  material: string; im_katalog: boolean; einheit: string;
  position_id: string; menge: number; abgerechnet: number; belege: number;
  zeilen_ids: string[];
  /** hand = von Hand zugeordnet, material = über das übernommene Angebotsmaterial abgeleitet. */
  zuordnung?: "hand" | "material" | "";
};

/** Lagebild für die Schlussrechnung — gerechnet, nicht bewertet. */
export type ProjektAbschluss = {
  projekt: { id: string; titel: string; status: string; kunde_name: string };
  angebot: {
    nummer?: string; status?: string; datum?: string; netto?: number; brutto?: number;
    positionen?: { titel: string; menge: number; einheit: string; einzelpreis_netto: number; gesamt_netto: number }[];
  };
  lieferungen: Lieferposition[];
  abschlaege: { nummer: string; art: string; status: string; datum: string; netto: number; brutto: number; bezahlt_am: string }[];
  arbeit: { termine: number; stunden: number; erster_tag: string; letzter_tag: string; fotos: Record<string, number> };
  gegenueberstellung: {
    zeilen: {
      position_id: string; titel: string; einheit: string; einzelpreis_netto: number;
      angeboten_menge: number; angeboten_netto: number;
      abrechenbar_menge: number; abrechenbar_netto: number;
      differenz_menge: number; differenz_netto: number;
      aufgemessen: boolean; notiz: string;
      /** false = von Hand ergänzt, weil das Angebot nur eine Summe trägt. */
      aus_angebot: boolean;
      belege: Lieferposition[];
      /** Gelieferte Menge in der Einheit der Position — null, wenn nichts oder nicht vergleichbar. */
      geliefert_menge?: number | null;
      /** true, solange „abrechenbar“ von der gelieferten Menge abweicht. */
      geliefert_weicht_ab?: boolean;
    }[];
    nachtraege: {
      id: string; titel: string; einheit: string; menge: number;
      einzelpreis_netto: number; netto: number; notiz: string;
    }[];
    summe_angeboten_netto: number;
    summe_abrechenbar_netto: number;
    summe_nachtraege_netto: number;
    offene_positionen: number;
    abweichend_von_lieferung?: number;
    lieferungen_ohne_position: Lieferposition[];
  };
  groessen: {
    auftragswert_netto: number; auftragswert_quelle: string;
    abzurechnen_netto: number; abzurechnen_quelle: string;
    abschlaege_netto: number; abschlaege_brutto: number; rest_rechnerisch_netto: number;
    mengenabgleich: unknown[];
    noch_nicht_abgerechnet: { material: string; einheit: string; menge: number; abgerechnet: number; offen: number }[];
  };
  luecken: string[];
  hinweis: string;
};

export function useProjektAbschluss(id: string | undefined, aktiv: boolean) {
  return useQuery<ProjektAbschluss>({
    queryKey: ["projekt", id, "abschluss"],
    queryFn: () => apiGet<ProjektAbschluss>(`/api/kontor/projekt/${id}/abschluss`),
    // Erst laden, wenn der Reiter offen ist: die Verdichtung fragt fünf Quellen
    // ab und gehört nicht in den Aufschlag jeder Projektseite.
    enabled: !!id && aktiv,
    staleTime: 30_000,
  });
}

/** Aufmaß setzen, Nachtrag anlegen/löschen — alle drei liefern das neue Lagebild. */
function useAbschlussMutation<T>(projektId: string, ruf: (v: T) => Promise<ProjektAbschluss>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ruf,
    // Die Antwort IST der neue Stand — kein zweiter Ladelauf, sonst flackert
    // die Tabelle bei jedem getippten Wert.
    onSuccess: (neu) => qc.setQueryData(["projekt", projektId, "abschluss"], neu),
  });
}

export function useAufmassSetzen(projektId: string) {
  return useAbschlussMutation(projektId, (v: { position_id: string; menge: number | null; notiz?: string }) =>
    apiPut<ProjektAbschluss>(`/api/kontor/projekt/${projektId}/abschluss/aufmass`, v));
}

export function useNachtragAnlegen(projektId: string) {
  return useAbschlussMutation(projektId, (v: {
    bezeichnung: string; menge: number; einheit: string; einzelpreis_netto: number; notiz?: string;
  }) => apiPost<ProjektAbschluss>(`/api/kontor/projekt/${projektId}/abschluss/nachtrag`, v));
}

export function useNachtragLoeschen(projektId: string) {
  return useAbschlussMutation(projektId, (id: string) =>
    apiDelete<ProjektAbschluss>(`/api/kontor/projekt/${projektId}/abschluss/nachtrag/${id}`));
}

export function useLieferungZuordnen(projektId: string) {
  return useAbschlussMutation(projektId, (v: { zeilen_ids: string[]; position_id: string }) =>
    apiPatch<ProjektAbschluss>(`/api/kontor/projekt/${projektId}/abschluss/lieferung`, v));
}

export function usePositionErgaenzen(projektId: string) {
  return useAbschlussMutation(projektId, (v: {
    bezeichnung: string; menge: number; einheit: string; einzelpreis_netto: number; notiz?: string;
  }) => apiPost<ProjektAbschluss>(`/api/kontor/projekt/${projektId}/abschluss/position`, v));
}

export function useSchlussrechnungEntwurf(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ entwurf_id: string; positionen: number; nachtraege: number; offene_positionen: number }>(
      `/api/kontor/projekt/${projektId}/abschluss/entwurf`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rechnungen"] }),
  });
}

export function useCreateProjektAkteEintrag(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjektAkteCreateBody) =>
      apiPost<ProjektAkteEintrag>(`/api/kontor/projekt/${projektId}/akte/eintraege`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projekt", projektId, "akte"] }),
  });
}

/** Phase einer vorhandenen Aufnahme korrigieren — der Umschalter beim Hochladen
 *  gilt für den ganzen Schwung, und darin ist manchmal ein Bild, das woanders
 *  hingehört. `notiz` bleibt unverändert, wenn sie nicht mitgeschickt wird. */
export function useUpdateProjektAufnahme(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notiz, phase }: { id: string; notiz: string; phase: AufnahmePhase }) => (
      apiPatch<ProjektAufnahme>(`/api/kontor/projekt/${projektId}/aufnahmen/${id}`, { notiz, phase })
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projekt", projektId, "akte"] }),
  });
}

/** Was von einem Schwung durchkam — und was nicht. */
/** `ohne_ort`: abgelegt, aber ohne Aufnahmeort in den Bilddaten — am Projekt erlaubt, nur ein Hinweis. */
export type AufnahmenErgebnis = { angelegt: ProjektAufnahme[]; abgelehnt: string[]; ohne_ort?: number };

/**
 * Mehrere Projektfotos in einem Zug hochladen.
 *
 * Zwölf Aufnahmen von einem Termin sind der Normalfall; eine Phase und eine
 * Notiz gelten für den ganzen Schwung. `arbeitstag` zählt nur bei
 * `phase="verlauf"` und bestimmt dort den Tagesordner in der Cloud.
 */
export function useCreateProjektAufnahme(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dateien, notiz, phase, arbeitstag }: {
      dateien: File[]; notiz: string; phase: AufnahmePhase; arbeitstag?: string;
    }) => {
      const form = new FormData();
      // Das Feld heisst weiterhin `datei` — mehrfach angehängt ist es die Liste.
      for (const d of dateien) form.append("datei", d);
      form.append("notiz", notiz);
      form.append("phase", phase);
      if (arbeitstag) form.append("arbeitstag", arbeitstag);
      return apiUpload<AufnahmenErgebnis>(`/api/kontor/projekt/${projektId}/aufnahmen`, form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projekt", projektId, "akte"] }),
  });
}

/** Ein Eintrag im Aktivitätsstrom — offen oder schon erledigt. */
export type AktivitaetItem = {
  slot_id: string;
  termin_id: string | null;
  person_name: string;
  projekt_title: string;
  datum: string;
  start_at: string;
  end_at: string;
  pause_stunden: number;
  stunden_netto: number;
  stundenlohn: number;
  confirm_status: string;
  /** 'aenderung' | 'bestaetigt' | 'kommentar' */
  kind: string;
  /** 'aenderung' | 'bewerbung' — entscheidet, welche Knöpfe gelten. */
  art: string;
  /** Delta (Vorher → Neu) bzw. Kommentartext des Helfers. */
  text: string;
  zeitpunkt: string;
};

export type Aktivitaet = {
  offen_count: number;
  offen: AktivitaetItem[];
  verlauf: AktivitaetItem[];
};

/**
 * Was aus der Team-App hereinkommt und auf eine Entscheidung wartet:
 * Bewerbungen auf offene Stellen und geänderte Arbeitszeiten.
 *
 * Wird auf der Planungsseite als Zähler am Knopf gezeigt, deshalb läuft die
 * Abfrage dort dauerhaft mit — sie ist klein und der Zähler wäre sonst falsch,
 * sobald jemand am Telefon etwas einträgt.
 */
export function useAktivitaet() {
  return useQuery({
    queryKey: ["kontor", "aktivitaet"],
    queryFn: () => apiGet<Aktivitaet>("/api/kontor/aktivitaet"),
    refetchInterval: 120_000,
  });
}

/** Antwort beider Foto-Wege: wie viele Bilder wohin gelegt wurden. */
export type TerminFotoErgebnis = {
  count: number;
  ordner: string;
  arbeitstag: string;
  projekt_id: string | null;
};

/**
 * Baustellenfotos an einem TERMIN ablegen (nicht an einer Person).
 *
 * Projekt und Arbeitstag kommen aus dem Termin — es gibt nichts einzugeben und
 * damit nichts falsch zu machen. Der Server legt sie unter „Verlauf/<Tag>" ab;
 * eine Phasenwahl gibt es bewusst nicht, Bilder von der laufenden Baustelle
 * sind der Verlauf.
 *
 * Ein Termin ohne Projekt (Besichtigung) wird mit 409 abgewiesen — dann fehlt
 * der Ablageort. Die Meldung dazu steht in `err.body.detail`, nicht in
 * `err.message`; die aufrufende Stelle muss sie sichtbar machen.
 */
export function useTerminFotos(terminId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dateien }: { dateien: File[] }) => {
      const form = new FormData();
      for (const d of dateien) form.append("dateien", d);
      return apiUpload<TerminFotoErgebnis>(`/api/kontor/planung/${terminId}/fotos`, form);
    },
    onSuccess: (res) => {
      // Die Bilder tauchen in der Projektakte auf — deren Verlauf neu holen.
      if (res.projekt_id) qc.invalidateQueries({ queryKey: ["projekt", res.projekt_id, "akte"] });
    },
  });
}

/**
 * Ein Termin haengt entweder an einem Projekt (Einsatz) ODER direkt an einem
 * Kunden (Besichtigung). Der Server verlangt eins von beidem; beides leer wird
 * mit 400 abgewiesen, weil so ein Termin spaeter nirgends wiederauftaucht.
 */
export type NewTerminBody = {
  projekt_id: string;
  kunde_id?: string;
  planung_typ?: "termin" | "besichtigung";
  title?: string;
  start_at: string;
  end_at: string;
  notiz?: string;
  benoetigte_helfer?: number;
  offene_stellen?: number;
  slots?: UpdateTerminSlot[];
  /** Antwort auf eine Rückfrage zu Doppelbelegungen (lib/terminKonflikt.ts). */
  konflikt_loesungen?: KonfliktLoesung[];
};

export function useCreateTermin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: NewTerminBody) => apiPost<Termin>("/api/kontor/planung", b),
    onSuccess: (_t, b) => {
      qc.invalidateQueries({ queryKey: ["kalender"] });
      qc.invalidateQueries({ queryKey: ["kontor", "team"] });
      // Einsatz geplant, Angebot noch offen? Das Blatt in der Shell fragt nach
      // (lib/angebotFrage.ts). Eine Besichtigung kommt vor dem Angebot.
      if (b.planung_typ !== "besichtigung") angebotFrageAnstossen(b.projekt_id);
    },
  });
}

export function useDeleteTermin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<{ ok: boolean }>(`/api/kontor/planung/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kalender"] }),
  });
}

/** Ein Einsatz (Slot) im Update-Body — komplette Slot-Liste ersetzt beim PATCH alle Slots. */
export type UpdateTerminSlot = {
  person_id: string;
  person_typ: "helfer" | "inhaber";
  start_at?: string;
  end_at?: string;
  pause_stunden?: number;
  stundenlohn?: number;
  zusatzkosten?: number;
  /**
   * Vergütungsart und Festpreis-Verknüpfung müssen mitgeschickt werden, auch
   * wenn der Planer sie nicht bearbeitet: der Server schreibt nur fort, was er
   * bekommt, und Weglassen wäre zwar unschädlich — Mitschicken hält die Absicht
   * aber sichtbar und schützt vor einem versehentlichen Umschalten.
   */
  verguetungsart?: "stundenlohn" | "pauschale";
  pauschale_id?: string | null;
  note?: string;
  confirm_status?: string;
  einladungs_status?: string;
};

export type UpdateTerminBody = Partial<{
  projekt_id: string;
  kunde_id: string;
  planung_typ: "termin" | "besichtigung";
  title: string;
  notiz: string;
  start_at: string;
  end_at: string;
  benoetigte_helfer: number;
  offene_stellen: number;
  slots: UpdateTerminSlot[];
  /** "" = Standard-Ort; sonst eine Herkunft aus useTerminOrte. */
  einsatzort_quelle: string;
  konflikt_loesungen: KonfliktLoesung[];
}>;

export function useUpdateTermin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; patch: UpdateTerminBody }) => apiPatch<Termin>(`/api/kontor/planung/${v.id}`, v.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kalender"] }),
  });
}

/** Orte, die für einen Termin in Frage kommen — Kunde, Projekt, Lieferanten. */
export function useTerminOrte(projektId: string, kundeId: string, enabled = true) {
  return useQuery<TerminOrte>({
    queryKey: ["termin-orte", projektId, kundeId],
    queryFn: () => apiGet<TerminOrte>(`/api/kontor/termin-orte?projekt_id=${encodeURIComponent(projektId)}&kunde_id=${encodeURIComponent(kundeId)}`),
    enabled,
    staleTime: 60_000,
  });
}

/** Adresse eines Lieferanten pflegen („Straße, PLZ Ort"). */
export function useLieferantAdresse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; adresse: string }) =>
      apiPatch<{ id: string; name: string; adresse: string }>(`/api/kontor/lieferanten/${encodeURIComponent(v.id)}/adresse`, { adresse: v.adresse }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["termin-orte"] }),
  });
}

export function useKundeDetail(id?: string) {
  return useQuery<KundeDetail>({ queryKey: ["kunde", id], queryFn: () => apiGet<KundeDetail>(`/api/kontor/kunde/${id}`), enabled: !!id, staleTime: 30_000 });
}
export function useCreateKunde() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: KundeCreateBody) => apiPost<Kunde>("/api/kontor/kunde", b), onSuccess: () => qc.invalidateQueries({ queryKey: ["kunden"] }) });
}
export function useUpdateKunde() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { id: string; patch: KundePatchBody }) => apiPatch<KundeDetail>(`/api/kontor/kunde/${v.id}`, v.patch), onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["kunden"] }); qc.invalidateQueries({ queryKey: ["kunde", d.id] }); } });
}

/** Schlanke Zeile eines verknüpften Projekts (Kundenblatt + Löschvorbedingung). */
export type KundeProjektRow = { id: string; title: string; status: string };

/** Pflegeverträge eines Kunden aus der Pflege-App (seit 09.10.2026) — dieselbe
 *  Quelle wie dort die Kundenseite (pflege_kunden_store.vertraege_des_kunden). */
export type KundePflegevertrag = {
  id: string; nummer: string; status: string; beginn: string | null;
  monats_netto: number | string | null; jahres_netto: number | string | null;
  gekuendigt_zum: string | null; version: number;
};
export function useKundePflegevertraege(id: string) {
  return useQuery<KundePflegevertrag[]>({ queryKey: ["kunde", id, "pflegevertraege"], queryFn: () => apiGet<KundePflegevertrag[]>(`/api/kontor/kunde/${id}/pflegevertraege`), enabled: !!id, staleTime: 60_000 });
}

export function useKundeProjekte(id?: string) {
  return useQuery<KundeProjektRow[]>({ queryKey: ["kunde", id, "projekte"], queryFn: () => apiGet<KundeProjektRow[]>(`/api/kontor/kunde/${id}/projekte`), enabled: !!id, staleTime: 30_000 });
}

export function useDeleteKunde() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiDelete<{ ok: boolean }>(`/api/kontor/kunde/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["kunden"] }) });
}
export function useCreateMember() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: TeamMemberCreateBody) => apiPost<TeamMember>("/api/kontor/team/member", b), onSuccess: () => qc.invalidateQueries({ queryKey: ["team"] }) });
}
export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { id: string; patch: TeamMemberPatchBody }) => apiPatch<TeamMember>(`/api/kontor/team/member/${v.id}`, v.patch), onSuccess: () => qc.invalidateQueries({ queryKey: ["team"] }) });
}

export function useCreateProjekt() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: ProjektCreateBody) => apiPost<ProjektDetail>("/api/kontor/projekt", b), onSuccess: () => qc.invalidateQueries({ queryKey: ["projekte"] }) });
}
export function useUpdateProjekt() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { id: string; patch: ProjektPatchBody }) => apiPatch<ProjektDetail>(`/api/kontor/projekt/${v.id}`, v.patch), onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["projekte"] }); qc.invalidateQueries({ queryKey: ["projekt", d.id] }); } });
}
export function useDeleteProjekt() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiDelete<{ deleted_termine: number }>(`/api/kontor/projekt/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["projekte"] }); qc.invalidateQueries({ queryKey: ["kalender"] }); } });
}


/** Tages-Events (Lieferungen/Werkzeug/Hinweise) eines Termins. from=to=Termindatum. */
export function useTerminEvents(terminId?: string, datum?: string) {
  return useQuery<TagesEvent[]>({
    queryKey: ["events", terminId],
    queryFn: () => apiGet<TagesEvent[]>(`/api/kontor/events?from=${datum}&to=${datum}&termin_id=${terminId}`),
    enabled: !!terminId && !!datum,
    staleTime: 15_000,
  });
}
export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: EventCreateBody) => apiPost<TagesEvent>("/api/kontor/event", b), onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }) });
}
export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { id: string; patch: EventPatchBody }) => apiPatch<TagesEvent>(`/api/kontor/event/${v.id}`, v.patch), onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }) });
}
export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiDelete<{ ok: boolean }>(`/api/kontor/event/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }) });
}


/** Helfer-Pauschalen eines Projekts (additiv zur Stundenbasis). */
export function useProjektPauschalen(id?: string) {
  return useQuery<Pauschale[]>({ queryKey: ["projekt", id, "pauschalen"], queryFn: () => apiGet<Pauschale[]>(`/api/kontor/projekt/${id}/pauschalen`), enabled: !!id, staleTime: 30_000 });
}
export function useAddPauschale() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { projektId: string; body: PauschaleCreateBody }) => apiPost<Pauschale>(`/api/kontor/projekt/${v.projektId}/pauschalen`, v.body), onSuccess: (_d, v) => { qc.invalidateQueries({ queryKey: ["projekt", v.projektId, "pauschalen"] }); qc.invalidateQueries({ queryKey: ["projekt", v.projektId, "kennzahlen"] }); } });
}
/**
 * Pauschale als verdient freigeben (oder die Freigabe zurücknehmen).
 *
 * Der bewusste Akt statt einer Fälligkeitsformel: ein Helfer nimmt für eine
 * Pauschale mehrere Termine in Anspruch, wann die Arbeit fertig ist, weiß nur
 * der Mensch davor (Entscheidung 12.08.2026). Erst danach zählt der Betrag im
 * Lohnkonto und der Helfer darf ihn berechnen.
 */
export function useFreigebenPauschale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { projektId: string; id: string; frei: boolean }) =>
      apiPost<Pauschale>(`/api/kontor/projekt/${v.projektId}/pauschalen/${v.id}/freigabe`, { frei: v.frei }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["projekt", v.projektId, "pauschalen"] });
      qc.invalidateQueries({ queryKey: ["projekt", v.projektId, "kennzahlen"] });
      qc.invalidateQueries({ queryKey: ["team"] });
    },
  });
}
/** Pauschale nachträglich anpassen (12.10.2026) — ID, Einsätze und Freigabe bleiben. */
export function usePatchPauschale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { projektId: string; id: string; body: { bezeichnung?: string; betrag?: number; datum?: string | null } }) =>
      apiPatch<Pauschale>(`/api/kontor/projekt/${v.projektId}/pauschalen/${v.id}`, v.body),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["projekt", v.projektId, "pauschalen"] });
      qc.invalidateQueries({ queryKey: ["projekt", v.projektId, "kennzahlen"] });
      qc.invalidateQueries({ queryKey: ["team"] });
    },
  });
}
export function useDeletePauschale() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { projektId: string; id: string }) => apiDelete<{ ok: boolean }>(`/api/kontor/projekt/${v.projektId}/pauschalen/${v.id}`), onSuccess: (_d, v) => { qc.invalidateQueries({ queryKey: ["projekt", v.projektId, "pauschalen"] }); qc.invalidateQueries({ queryKey: ["projekt", v.projektId, "kennzahlen"] }); } });
}

// ─── Projektübersicht (Projektseite: Karten, KPIs, Jahresfilter) ───────────

/** Reiche Projektliste — Nettowert, Live-Kosten, Beleg-Tags, Jahresfilter. */
export function useProjekteUebersicht(year: number | "all", enabled = true) {
  return useQuery<ProjektUebersicht[]>({
    enabled,
    queryKey: ["projekte", "uebersicht", year],
    queryFn: () =>
      apiGet<ProjektUebersicht[]>(
        `/api/kontor/projekte/uebersicht${year === "all" ? "" : `?year=${year}`}`,
      ),
    staleTime: 30_000,
  });
}

/** Kanonische Projektstatus — Spiegel von crm/config.yaml `status_options`. */
export function useProjektStatusOptions() {
  return useQuery<string[]>({
    queryKey: ["projekte", "status-options"],
    queryFn: () => apiGet<string[]>("/api/kontor/projekte/status-options"),
    staleTime: 60 * 60_000,
  });
}

/**
 * Status direkt auf der Projektkarte umschalten — optimistisch wie im Desktop:
 * die Karte wechselt sofort die Gruppe, bei Fehler wird zurückgerollt.
 */
export function useSetProjektStatus(year: number | "all") {
  const qc = useQueryClient();
  const key = ["projekte", "uebersicht", year];
  return useMutation({
    mutationFn: (v: { id: string; status: string }) =>
      apiPatch<ProjektUebersicht>(`/api/kontor/projekt/${v.id}/status`, { status: v.status }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ProjektUebersicht[]>(key);
      qc.setQueryData<ProjektUebersicht[]>(key, (old) =>
        old?.map((p) => (p.id === v.id ? { ...p, status: v.status } : p)));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSuccess: (row) => {
      qc.setQueryData<ProjektUebersicht[]>(key, (old) =>
        old?.map((p) => (p.id === row.id ? row : p)));
      qc.invalidateQueries({ queryKey: ["projekt", row.id] });
      qc.invalidateQueries({ queryKey: ["projekte"] });
    },
  });
}

// ─── Projekt-Ausgaben + Stundenzeilen (Projektdetail) ──────────────────────

/** Alle Ausgaben eines Projekts — Material und Verpflegung in einer Liste. */
export function useProjektAusgaben(projektId?: string) {
  return useQuery<Ausgabe[]>({
    queryKey: ["projekt", projektId, "ausgaben"],
    queryFn: () => apiGet<Ausgabe[]>(`/api/kontor/projekt/${projektId}/ausgaben`),
    enabled: !!projektId,
    staleTime: 30_000,
  });
}

/**
 * Auftragswert, Zahlungsstand und Statusvorschlag eines Projekts.
 *
 * Wird nach jedem Beleg und jeder Ausgabe neu gezogen, weil sich der Wert beim
 * Festschreiben serverseitig ändert — eine zwischengespeicherte Zahl wäre hier
 * schnell falsch.
 */
export function useProjektAuftragswert(projektId?: string) {
  return useQuery<ProjektAuftragswert>({
    queryKey: ["projekt", projektId, "auftragswert"],
    queryFn: () => apiGet<ProjektAuftragswert>(`/api/kontor/projekt/${projektId}/auftragswert`),
    enabled: !!projektId,
    staleTime: 15_000,
  });
}

/**
 * Rechnungen desselben Kunden, die an keinem Projekt hängen.
 *
 * Der häufigste Grund für einen zu niedrigen Auftragswert ist keine fehlende
 * Leistung, sondern eine fehlende Zuordnung — im August 2026 lagen 35 Rechnungen
 * über 63.287 € ohne Projektbezug herum.
 */
export function useProjektRechnungsKandidaten(projektId?: string) {
  return useQuery<RechnungKandidat[]>({
    queryKey: ["projekt", projektId, "rechnungen-kandidaten"],
    queryFn: () => apiGet<RechnungKandidat[]>(`/api/kontor/projekt/${projektId}/rechnungen-kandidaten`),
    enabled: !!projektId,
    staleTime: 30_000,
  });
}

/** Eine herrenlose Rechnung diesem Projekt zuordnen. */
export function useRechnungZuordnen(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rechnungId: string) =>
      apiPatch<unknown>(`/api/kontor/rechnungen/${rechnungId}`, { projekt_id: projektId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projekt", projektId] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["projekte"] });
    },
  });
}

/**
 * Belege und Lieferscheine eines Projekts.
 *
 * Eigener Endpunkt statt `/belege?projekt_id=`: der filtert nur über
 * `beleg.projekt_id` und übersieht damit Lieferscheine, die über die
 * M:N-Tabelle an mehreren Projekten hängen.
 */
export function useProjektBelege(projektId?: string) {
  return useQuery<ProjektBeleg[]>({
    queryKey: ["projekt", projektId, "belege"],
    queryFn: () => apiGet<ProjektBeleg[]>(`/api/kontor/projekt/${projektId}/belege`),
    enabled: !!projektId,
    staleTime: 30_000,
  });
}

/** Nach jeder Ausgaben-Mutation ziehen Kennzahlen und Projektliste nach. */
function useAusgabenInvalidierung(projektId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["projekt", projektId, "ausgaben"] });
    qc.invalidateQueries({ queryKey: ["projekt", projektId, "kennzahlen"] });
    qc.invalidateQueries({ queryKey: ["projekte"] });
  };
}

export function useCreateAusgabe(projektId: string) {
  const nachziehen = useAusgabenInvalidierung(projektId);
  return useMutation({
    mutationFn: (b: AusgabeCreateBody) =>
      apiPost<Ausgabe>(`/api/kontor/projekt/${projektId}/ausgaben`, b),
    onSuccess: nachziehen,
  });
}

export function useUpdateAusgabe(projektId: string) {
  const nachziehen = useAusgabenInvalidierung(projektId);
  return useMutation({
    mutationFn: (v: { id: string; patch: AusgabePatchBody }) =>
      apiPatch<Ausgabe>(`/api/kontor/projekt/${projektId}/ausgaben/${v.id}`, v.patch),
    onSuccess: nachziehen,
  });
}

export function useDeleteAusgabe(projektId: string) {
  const nachziehen = useAusgabenInvalidierung(projektId);
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete<{ deleted: number }>(`/api/kontor/projekt/${projektId}/ausgaben/${id}`),
    onSuccess: nachziehen,
  });
}

/** Einzelne Stundenzeilen eines Projekts (die Kennzahlen liefern nur Summen). */
export function useProjektStunden(projektId?: string) {
  return useQuery<StundenZeile[]>({
    queryKey: ["projekt", projektId, "stunden"],
    queryFn: () => apiGet<StundenZeile[]>(`/api/kontor/projekt/${projektId}/stunden`),
    enabled: !!projektId,
    staleTime: 30_000,
  });
}

/** Nach Slot-Änderungen hängen Stunden, Kennzahlen, Liste und Kalender dran. */
function useSlotInvalidierung(projektId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["projekt", projektId, "stunden"] });
    qc.invalidateQueries({ queryKey: ["projekt", projektId, "kennzahlen"] });
    qc.invalidateQueries({ queryKey: ["projekte"] });
    qc.invalidateQueries({ queryKey: ["kalender"] });
  };
}

export function useUpdateSlot(projektId: string) {
  const nachziehen = useSlotInvalidierung(projektId);
  return useMutation({
    mutationFn: (v: { slotId: string; patch: SlotPatchBody }) =>
      apiPatch<unknown>(`/api/kontor/slots/${v.slotId}`, v.patch),
    onSuccess: nachziehen,
  });
}

/** Freie Bewerbung ablehnen; der Slot bleibt als nachvollziehbare Historie erhalten. */
export function useRejectSlot(projektId: string) {
  const nachziehen = useSlotInvalidierung(projektId);
  return useMutation({
    mutationFn: (v: { slotId: string; kommentar?: string }) =>
      apiPost<unknown>(`/api/kontor/slots/${v.slotId}/ablehnen`, {
        kommentar: v.kommentar ?? "",
      }),
    onSuccess: nachziehen,
  });
}

/**
 * Einen Einsatz nachtragen — Inhaber wie Helfer.
 *
 * Der Server hängt ihn an den Termin des Tages oder legt einen an; deshalb
 * zieht auch der Kalender nach, nicht nur die Stundenliste.
 */
export function useCreateStunde(projektId: string) {
  const nachziehen = useSlotInvalidierung(projektId);
  return useMutation({
    mutationFn: (body: StundeCreateBody) =>
      apiPost<StundenZeile>(`/api/kontor/projekt/${projektId}/stunden`, body),
    onSuccess: nachziehen,
  });
}

/**
 * Einsätze auf „bestätigt" setzen — eine Zeile oder ein ganzer Monat.
 *
 * Bewusst nicht über `PATCH /slots/{id}`: der schreibt `bestaetigt_am` nicht
 * mit, und die Team-PWA erwartet das Feld.
 */
export function useBestaetigeStunden(projektId: string) {
  const nachziehen = useSlotInvalidierung(projektId);
  return useMutation({
    mutationFn: (slotIds: string[]) =>
      apiPost<{ ok: boolean; bestaetigt: number }>(
        `/api/kontor/projekt/${projektId}/stunden/bestaetigen`,
        { slot_ids: slotIds },
      ),
    onSuccess: nachziehen,
  });
}

export function useDeleteSlot(projektId: string) {
  const nachziehen = useSlotInvalidierung(projektId);
  return useMutation({
    mutationFn: (slotId: string) => apiDelete<unknown>(`/api/kontor/slots/${slotId}`),
    onSuccess: nachziehen,
  });
}

/** Termine eines Projekts — lesend; geaendert wird in der Planungsansicht. */
export function useProjektTermine(projektId?: string) {
  return useQuery<ProjektTermin[]>({
    queryKey: ["projekt", projektId, "termine"],
    queryFn: () => apiGet<ProjektTermin[]>(`/api/kontor/projekt/${projektId}/termine`),
    enabled: !!projektId,
    staleTime: 30_000,
  });
}
